import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';
const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

// Brand → its categories → a category's sub categories → a sub category's
// page, which ends in a Contact Us call to action. All three levels share
// this component; which one shows depends on how many URL params are set.
//   /brands/:slug                              → category grid
//   /brands/:slug/:categorySlug                → sub-category grid
//   /brands/:slug/:categorySlug/:subSlug       → contact page for that item

function CardGrid({ items, linkFor }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
      {items.map((item, i) => (
        <Link key={item.id} to={linkFor(item)} className="group block fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
          <div className="aspect-[4/3] bg-[#f5f5f5] overflow-hidden mb-4">
            {item.image_url ? (
              <img src={imgSrc(item.image_url)} alt={item.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-charcoal">
                <span className="text-white/20 text-6xl font-display font-extrabold">{item.name[0]}</span>
              </div>
            )}
          </div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-bold text-ink text-base sm:text-lg leading-snug group-hover:text-primary transition-colors mb-1.5">{item.name}</h3>
              {item.description && <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 leading-relaxed">{item.description}</p>}
            </div>
            <span className="mt-2 w-2 h-2 shrink-0 border-t-2 border-r-2 border-primary rotate-45 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      ))}
    </div>
  );
}

function ContactPanel({ title, about }) {
  const { t } = useLanguage();
  return (
    <div className="bg-[#f5f5f5] p-8 sm:p-12 text-center">
      <h3 className="text-xl sm:text-2xl font-bold text-ink mb-3">{t('brands.interestedIn').replace('{name}', title)}</h3>
      <p className="text-gray-600 text-sm mb-7 max-w-lg mx-auto">{t('brands.contactPrompt')}</p>
      <Link to={`/contact?about=${encodeURIComponent(about)}`} className="btn-primary btn-arrow text-sm min-w-[200px] justify-between">
        {t('common.contactUs')}
      </Link>
    </div>
  );
}

export default function BrandCatalog() {
  const { t } = useLanguage();
  const { slug, categorySlug, subSlug } = useParams();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    API.get(`/brands/by-slug/${slug}`)
      .then(r => setBrand(r.data))
      .catch(() => setBrand(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const category = categorySlug ? brand?.categories.find(c => c.slug === categorySlug) : null;
  const sub = subSlug ? category?.children.find(c => c.slug === subSlug) : null;
  const notFound = !loading && (!brand || (categorySlug && !category) || (subSlug && !sub));

  const brandPath = `/brands/${slug}`;
  const categoryPath = `${brandPath}/${categorySlug}`;
  const current = sub || category || brand;

  const breadcrumbs = [{ label: t('common.brands'), to: '/brands' }];
  if (brand) breadcrumbs.push({ label: brand.name, to: categorySlug ? brandPath : undefined });
  if (category) breadcrumbs.push({ label: category.name, to: subSlug ? categoryPath : undefined });
  if (sub) breadcrumbs.push({ label: sub.name });

  const backTo = subSlug ? categoryPath : categorySlug ? brandPath : '/brands';
  const backLabel = subSlug ? category?.name : categorySlug ? brand?.name : t('common.brands');

  return (
    <PageLayout>
      <SEO title={current?.name} description={current?.description || undefined} />
      <PageBanner en="OUR BRANDS" title={current?.name || t('common.brands')} breadcrumbs={breadcrumbs} />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[4/3] bg-gray-100 animate-pulse" />)}
            </div>
          ) : notFound ? (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg mb-6">{t('brands.notFound')}</p>
              <Link to="/brands" className="btn-primary text-sm">{t('brands.backToBrands')}</Link>
            </div>
          ) : (
            <>
              <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors mb-8">
                <ArrowLeftIcon className="w-3.5 h-3.5" /> {t('common.backTo')} {backLabel}
              </Link>

              {/* Intro: logo/image + description of whatever level we're on */}
              {(current.logo_url || current.image_url || current.description) && (
                <div className="flex flex-col sm:flex-row gap-8 items-start mb-14">
                  {(current.logo_url || current.image_url) && (
                    <div className={`shrink-0 border border-gray-200 flex items-center justify-center ${current.logo_url ? 'w-48 h-28 p-4' : 'w-full sm:w-80 aspect-[4/3] overflow-hidden'}`}>
                      <img src={imgSrc(current.logo_url || current.image_url)} alt={current.name}
                        className={current.logo_url ? 'max-w-full max-h-full object-contain' : 'w-full h-full object-cover'} />
                    </div>
                  )}
                  {current.description && <p className="text-gray-600 leading-loose whitespace-pre-line max-w-3xl">{current.description}</p>}
                </div>
              )}

              {sub ? (
                <ContactPanel title={sub.name} about={`${brand.name} / ${category.name} / ${sub.name}`} />
              ) : category ? (
                category.children.length > 0 ? (
                  <>
                    <h2 className="section-title mb-10">{t('brands.subcategories')}</h2>
                    <CardGrid items={category.children} linkFor={c => `${categoryPath}/${c.slug}`} />
                  </>
                ) : (
                  <ContactPanel title={category.name} about={`${brand.name} / ${category.name}`} />
                )
              ) : brand.categories.length > 0 ? (
                <>
                  <h2 className="section-title mb-10">{t('brands.categories')}</h2>
                  <CardGrid items={brand.categories} linkFor={c => `${brandPath}/${c.slug}`} />
                </>
              ) : (
                <ContactPanel title={brand.name} about={brand.name} />
              )}
            </>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
